import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { DateTime } from "luxon";
import { type FC, Fragment, type MouseEvent, useCallback, useLayoutEffect, useRef, useState } from "react";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { CONTROL_HEIGHT, FONT_SIZE, INK, NEUTRAL } from "@/theme/tokens";
import { SystemDatePickerPopup } from "../Popups/SystemDatePickerPopup";
import { SystemValidationTextField } from "./SystemValidationTextField";

type Props = {
	/** Override the chosen department when editing a different department or another timezone. */
	timezone?: string;
	value?: DateTime;
	onChange?: (newValue: DateTime) => void;
	displaySystemPickerOnClick?: boolean;
	minDate?: DateTime;
	maxDate?: DateTime;
	label?: string;
	placeholder?: string;
	required?: boolean;
	shouldDisableDate?: DatePickerProps["shouldDisableDate"];
	fontSx?: SxProps<Theme>;
	fullHeight?: boolean;
	inputSx?: SxProps<Theme>;
	formControlSx?: SxProps<Theme>;
	disabled?: boolean;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	datePickerToolbarTitle?: string;
	dataTestId?: string;
	onClear?: () => void;
	/** See `SystemValidationTextField`'s own doc — keeps the clear icon's width reserved even while there's no date, so this field's width doesn't jump when a date is picked/cleared. Off by default. */
	reserveClearWidth?: boolean;
	/**
	 * Dense variant for a narrow column or a toolbar: the value at `FONT_SIZE.SECONDARY` in a
	 * `CONTROL_HEIGHT.COMPACT` box, with the browser's own calendar glyph scaled to match.
	 *
	 * Font size is the load-bearing part, not decoration. A `type="date"` input is sized
	 * INTRINSICALLY — the browser measures the widest date it can hold at the current font — so at the
	 * standard 16px it demands ~167px and two of them cannot share a 274px column. Shrinking the font
	 * is what actually makes the field narrower; a `width` on its own just clips the value.
	 */
	compact?: boolean;
};

const ensureDateInRange = ({
	minDate,
	maxDate,
	date,
	onChange,
}: {
	date: DateTime;
	minDate?: DateTime;
	maxDate?: DateTime;
	onChange?: (newValue: DateTime) => void;
}) => {
	if (!onChange) return;
	// Only adjust if date is actually outside the valid range
	if (maxDate && date.startOf("day") > maxDate.startOf("day")) {
		onChange(maxDate);
	} else if (minDate && date.startOf("day") < minDate.startOf("day")) {
		onChange(minDate);
	}
};

export const DateInput: FC<Props> = ({
	timezone: timezoneOverride,
	displaySystemPickerOnClick = true,
	value,
	onChange,
	minDate,
	maxDate,
	placeholder,
	label,
	required,
	shouldDisableDate,
	fontSx,
	fullHeight = false,
	inputSx,
	disabled,
	open: controlledOpen,
	onOpenChange,
	formControlSx,
	datePickerToolbarTitle,
	dataTestId,
	onClear,
	reserveClearWidth,
	// DEFAULT: the standard 40px field, so every existing caller is untouched.
	compact = false,
}) => {
	const departmentFlags = useDepartmentFlags();
	// DEFAULT: existing callers use the chosen department; forms may explicitly target another timezone.
	const timezone = timezoneOverride ?? departmentFlags.timezone;
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const inputRef = useRef<HTMLElement | null>(null);

	const isOpen = (controlledOpen !== undefined ? controlledOpen : !!anchorEl) && !!anchorEl;

	useLayoutEffect(() => {
		if (controlledOpen === true && !anchorEl && inputRef.current) {
			setAnchorEl(inputRef.current);
		} else if (controlledOpen === false && anchorEl) {
			setAnchorEl(null);
		}
	}, [controlledOpen, anchorEl]);

	const handleChange = useCallback(
		(newValue: DateTime | null) => {
			if (newValue && onChange) {
				onChange(newValue.setZone(timezone));
				setAnchorEl(null);
				onOpenChange?.(false);
				setTimeout(
					() =>
						ensureDateInRange({
							onChange,
							date: newValue.setZone(timezone),
							maxDate,
							minDate,
						}),
					300,
				);
			}
		},
		[onChange, timezone, maxDate, minDate, onOpenChange],
	);

	return (
		<Fragment>
			<SystemValidationTextField
				dataTestId={dataTestId}
				disabled={disabled}
				reserveHeightForHelperText={false}
				placeholder={placeholder}
				required={required}
				label={label}
				value={value?.toFormat("yyyy-MM-dd") ?? ""}
				inputType={"date"}
				formControlSx={formControlSx}
				labelSx={{
					color: alpha(INK.SECONDARY, 0.65),
					...(compact ? { fontSize: FONT_SIZE.SECONDARY } : {}),
				}}
				onClear={onClear}
				reserveClearWidth={reserveClearWidth}
				onChange={(event) => {
					event.stopPropagation();
					event.preventDefault();
					// An explicit target zone must preserve the entered calendar date even when the chosen department differs.
					const parsedDate = event.target.value
						? timezoneOverride === undefined
							? DateTime.fromFormat(event.target.value, "yyyy-MM-dd").setZone(timezone)
							: DateTime.fromFormat(event.target.value, "yyyy-MM-dd", { zone: timezone })
						: null;
					if (parsedDate?.isValid) {
						handleChange(parsedDate);
					} else if (!event.target.value) {
						// Native date editing can clear the value without pressing the explicit clear button.
						onClear?.();
					}
				}}
				injectedProps={{
					slotProps: {
						input: {
							sx: {
								overflow: "hidden",
								...(compact ? { fontSize: FONT_SIZE.SECONDARY, minHeight: CONTROL_HEIGHT.COMPACT, minWidth: 0 } : {}),
								...fontSx,
							},
						},
						htmlInput: {
							ref: inputRef,
							onClick: (event: MouseEvent<HTMLElement>) => {
								event.stopPropagation();
								event.preventDefault();
								const newOpen = !isOpen && displaySystemPickerOnClick;
								setAnchorEl(newOpen ? event.currentTarget : null);
								onOpenChange?.(newOpen);
							},
							sx: {
								":hover": {
									cursor: displaySystemPickerOnClick ? "pointer" : "default",
									background: NEUTRAL[100],
								},
								...(fullHeight
									? {
											height: "100%",
										}
									: {}),
								...(compact
									? {
											py: 0.5,
											// A `type="date"` input carries an intrinsic min-width from its own font, which
											// otherwise floors the field and makes a flex parent overflow instead of shrink.
											minWidth: 0,
											width: "100%",
											// The native glyph doesn't scale with the font, so it has to be scaled by hand or
											// it ends up dominating a field this short.
											"&::-webkit-calendar-picker-indicator": { transform: "scale(0.85)", padding: 0, marginInlineStart: 0 },
										}
									: {}),
								...inputSx,
							},
						},
					},
				}}
			/>
			{anchorEl && (
				<SystemDatePickerPopup
					shouldDisableDate={shouldDisableDate}
					overridePickerProps={{
						minDate,
						maxDate,
						...(timezoneOverride === undefined ? {} : { timezone }),
					}}
					open={isOpen}
					anchorEl={anchorEl}
					onChange={handleChange}
					value={value ?? null}
					datePickerToolbarTitle={datePickerToolbarTitle}
					onClose={() => {
						setAnchorEl(null);
						onOpenChange?.(false);
					}}
				/>
			)}
		</Fragment>
	);
};
